import { Flex, Link, LinkExternal, Skeleton, Text, TimerIcon, Balance, Pool } from '@pancakeswap/uikit'
import AddToWalletButton, { AddToWalletTextOptions } from 'components/AddToWallet/AddToWalletButton'
import { useTranslation } from '@pancakeswap/localization'
import { Token } from '@pancakeswap/sdk'
import { BIG_ZERO } from '@pancakeswap/utils/bigNumber'
import { memo } from 'react'
import { useCurrentBlock } from 'state/block/hooks'
import { useDeserializedPoolByVaultKey, useVaultPoolByKey } from 'state/pools/hooks'
import { DeserializedLockedVaultUser, Flexible2Locked, IsCakeVault, IsLocked, Locked2Flexible } from 'state/types'
import { getBlockExploreLink } from 'utils'
import { getAddress } from 'utils/addressHelpers'
import { useActiveChainId } from 'hooks/useActiveChainId'
import { getPoolBlockInfo } from 'views/Pools/helpers'
import MaxStakeRow from './MaxStakeRow'
import { AprInfo, DurationAvg, PerformanceFee, TotalLocked } from './Stat'

interface ExpandedFooterProps {
  pool: Pool.DeserializedPool<Token>
  account: string
  showTotalStaked?: boolean
  alignLinksToRight?: boolean
}

const PoolStatsInfo: React.FC<React.PropsWithChildren<ExpandedFooterProps>> = ({
  pool,
  account,
  showTotalStaked = true,
  alignLinksToRight = true,
}) => {
  const { t } = useTranslation()
  const currentBlock = useCurrentBlock()
  const { chainId } = useActiveChainId()

  const {
    totalStaked, 
    stakingToken,
    earningToken,
    startBlock,
    endBlock,
    stakingLimit,
    stakingLimitEndBlock,
    contractAddress,
    vaultKey,
    isFinished,
    userData: poolUserData,
  } = pool

  const stakedBalance = poolUserData?.stakedBalance ? poolUserData.stakedBalance : BIG_ZERO

  const {
    totalCakeInVault,
    fees: { performanceFeeAsDecimal },
    userData,
  } = useVaultPoolByKey(vaultKey)

  const tokenAddress = stakingToken.address || ''
  const poolContractAddress = getAddress(contractAddress, chainId)
  // const cakeVaultContractAddress = poolContractAddress // getVaultPoolAddress(Flexible2Locked(vaultKey) ?? vaultKey)

  const { shouldShowBlockCountdown, blocksUntilStart, blocksRemaining, hasPoolStarted, blocksToDisplay } =
    getPoolBlockInfo(pool, currentBlock)

  const poolVault = useDeserializedPoolByVaultKey(Flexible2Locked(vaultKey))
  const { totalStaked: totalFlexibleAmount, totalLockedAmount: totalLocked } = poolVault as any
  const isVault = !!vaultKey
  const isCakeVault = IsCakeVault(vaultKey)
  const poolFlexible = useVaultPoolByKey(Locked2Flexible(vaultKey))
  const isLockedVault = IsLocked(vaultKey) && (userData as DeserializedLockedVaultUser).locked
  const totalCakeInFlexibleVault = isCakeVault && isLockedVault ? poolFlexible.totalCakeInVault : BIG_ZERO

  const totalLockedAmount = isCakeVault ? totalCakeInVault.minus(totalCakeInFlexibleVault) : totalLocked
  const totalStakedAmount = isVault ? isCakeVault ? totalCakeInVault : totalFlexibleAmount.minus(totalLocked) : totalStaked

  // console.log(pool.sousId, totalStakedAmount)

  return (
    <>
      {!vaultKey && <AprInfo pool={pool} stakedBalance={stakedBalance} />}
      {showTotalStaked && !isLockedVault && (
        <Pool.TotalStaked
          totalStaked={totalStakedAmount}
          tokenDecimals={stakingToken.decimals}
          symbol={stakingToken.symbol}
          decimalsToShow={0}
        />
      )}
      {isLockedVault && <TotalLocked totalLocked={totalLockedAmount} lockedToken={stakingToken} />}
      {isLockedVault && <DurationAvg vaultKey={vaultKey}/>}
      {!isFinished && stakingLimit && stakingLimit.gt(0) && (
        <MaxStakeRow
          small
          currentBlock={currentBlock}
          hasPoolStarted={hasPoolStarted}
          stakingLimit={stakingLimit}
          stakingLimitEndBlock={stakingLimitEndBlock}
          stakingToken={stakingToken}
          endBlock={endBlock}
        />
      )}
      {shouldShowBlockCountdown && (
        <Flex mb="2px" justifyContent="space-between" alignItems="center">
          <Text small>{hasPoolStarted ? t('Ends in') : t('Starts in')}:</Text>
          {blocksRemaining || blocksUntilStart ? (
            <Flex alignItems="center">
              <Link external href={getBlockExploreLink(hasPoolStarted ? endBlock : startBlock, 'countdown')}>
                <Balance small value={blocksToDisplay} decimals={0} color="primary" />
                <Text small ml="4px" color="primary" textTransform="lowercase">
                  {t('Blocks')}
                </Text>
                <TimerIcon ml="4px" color="primary" />
              </Link>
            </Flex>
          ) : (
            <Skeleton width="54px" height="21px" />
          )}
        </Flex>
      )}
      {vaultKey && <PerformanceFee userData={userData} performanceFeeAsDecimal={performanceFeeAsDecimal} />}
      <Flex mb="2px" justifyContent={alignLinksToRight ? 'flex-start' : 'flex-end'}>
        <LinkExternal href={`/info/token/${earningToken.address}`} bold={false} small>
          {t('See Token Info')}
        </LinkExternal>
      </Flex>
    </>
  )
}

export default memo(PoolStatsInfo)
